// Utility functions for formatting data in the Reports component

// Shown wherever a metric was never measured. Not every channel reports every
// metric (only YouTube reports revenue and impressions, for instance), and
// rendering those as 0 asserts a number the provider never gave us.
export const UNKNOWN = '—';

// null/undefined mean "not measured"; 0 is a real, measured zero.
export const isUnknown = (value) => value === null || value === undefined || Number.isNaN(value);

export const formatNumber = (num) => {
    if (isUnknown(num)) return UNKNOWN;

    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1) + 'B';
    } else if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
};

export const formatCurrency = (amount) => {
    if (isUnknown(amount)) return UNKNOWN;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

export const formatPercentage = (value) => {
    if (isUnknown(value)) return UNKNOWN;
    return value.toFixed(2) + '%';
};

// Sum that treats unknowns as absent rather than zero: returns null when no
// contributor reported the metric, so a total of "nothing known" stays unknown.
export const sumKnown = (values) => {
    const known = values.filter((v) => !isUnknown(v));
    return known.length ? known.reduce((sum, v) => sum + v, 0) : null;
};

export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const formatDateFromTicks = (ticks) => {
    if (!ticks && ticks !== 0) return 'N/A';
    
    // Convert ticks to milliseconds
    // Ticks are 100-nanosecond intervals since January 1, 0001 UTC
    // JavaScript Date epoch is January 1, 1970 UTC
    
    // Number of ticks from January 1, 0001 to January 1, 1970
    const ticksToUnixEpoch = 621355968000000000;
    
    // Convert to Unix timestamp in milliseconds
    const unixTimestampMs = (ticks - ticksToUnixEpoch) / 10000;
    
    const date = new Date(unixTimestampMs);
    
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Named for .NET ticks, but the API now returns Firestore timestamps as ISO
// strings — subtracting the tick epoch from one of those is NaN, which is why
// these read "Invalid Date" on screen. Accept whichever form arrives.
export const formatDateTimeFromTicks = (value) => {
    if (!value && value !== 0) return 'N/A';

    const ticksToUnixEpoch = 621355968000000000;
    let date;
    if (typeof value === 'number') {
        // Ticks are 100ns units since year 1, so they dwarf a ms epoch.
        date = new Date(value > 1e15 ? (value - ticksToUnixEpoch) / 10000 : value);
    } else {
        date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Helper function to convert date to UTC ISO string for API calls
export const toUTCISOString = (date) => {
    if (!date) return null;
    return new Date(date).toISOString();
};

// Ratios are unknown -- not zero -- unless both operands were measured. A 0%
// CTR reads as "nobody clicked"; the truth is usually "impressions were never
// reported by this channel".
export const calculateCTR = (clicks, impressions) => {
    if (isUnknown(clicks) || isUnknown(impressions)) return null;
    if (!impressions) return null;
    return (clicks / impressions) * 100;
};

export const calculateRPM = (earnings, impressions) => {
    if (isUnknown(earnings) || isUnknown(impressions)) return null;
    if (!impressions) return null;
    return (earnings / impressions) * 1000;
};

export const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
        case 'published':
        case 'live':
            return 'status-published';
        case 'draft':
            return 'status-draft';
        case 'scheduled':
            return 'status-scheduled';
        case 'archived':
            return 'status-archived';
        default:
            return 'status-draft';
    }
};

export const getPerformanceColor = (value, type) => {
    switch (type) {
        case 'ctr':
            if (value > 5) return '#48bb78'; // Green
            if (value > 2) return '#ed8936'; // Orange
            return '#e53e3e'; // Red
        case 'earnings':
            if (value > 100) return '#48bb78';
            if (value > 10) return '#ed8936';
            return '#e53e3e';
        default:
            return '#4299e1';
    }
};
